// src/contexts/AuthContext.js
import React, { useContext, useState, useEffect } from "react";
import { auth } from "../firebase-config";
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    updateProfile
} from "firebase/auth";

const AuthContext = React.createContext();

export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider({ children }) {
    const [currentUser, setCurrentUser] = useState();
    const [loading, setLoading] = useState(true);

    async function signup(email, password, name) {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        if (name) {
            await updateProfile(userCredential.user, { displayName: name });
        }
        return userCredential;
    }

    function login(email, password) {
        return signInWithEmailAndPassword(auth, email, password);
    }

    function logout() {
        return signOut(auth);
    }

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            setCurrentUser(user);

            // Immediately extract tier from JWT claims for faster UI rendering
            if (user) {
                try {
                    const tokenResult = await user.getIdTokenResult();
                    const tier = tokenResult.claims.tier || 'free';
                    // Set tier immediately in profileStore (before /app-context completes)
                    const { useProfileStore } = await import('../stores/profileStore');
                    useProfileStore.setState({ userTier: tier });
                } catch (err) {
                    console.warn('Could not extract tier from JWT claims:', err);
                }
            }

            setLoading(false);
        });

        return unsubscribe;
    }, []);

    async function forceTokenRefresh() {
        if (auth.currentUser) {
            return await auth.currentUser.getIdToken(true);
        }
    }

    const value = {
        currentUser,
        login,
        signup,
        logout,
        getIdToken: () => currentUser?.getIdToken(),
        forceTokenRefresh
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
}
